import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { Fir8858ScheduleMService } from './fir-8858-schedule-m.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface ColumnData {
  VALUE: number | null;
  line_attrib_key: string;
  attrib_name: string;
  trans_Details_key: string;
  occurrence: string;
  is_Update: string;
}

interface ScheduleMLineItem {
  line_no: number;
  line_multiplier: number;
  line_Description: string;
  backgroudColor?: any;
  total_line_type?: string;
  line_type: string;
  col_b1?: ColumnData;
  col_b?: ColumnData;
  col_c1?: ColumnData;
  col_c?: ColumnData;
  col_d1?: ColumnData;
  col_d?: ColumnData;
  col_e?: ColumnData;
  col_f?: ColumnData;
  saveParams: {
    tax_year: string;
    trans_type_key: string;
    form_key: string;
  };
}

interface AdjustmentData {
  LINE_NO: number;
  B_AMT?: number;
  C_AMT?: number;
  D_AMT?: number;
  E_AMT?: number;
  F_AMT?: number;
}

@Component({
    selector: 'gtw-fir-8858-schedule-m',
    templateUrl: './fir-8858-schedule-m.component.html',
    styleUrls: ['./fir-8858-schedule-m.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Fir8858ScheduleMComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private unsubscribe$ = new Subject<void>();
  private loadCancel$ = new Subject<void>();
  private subscriptions = new Subscription();
  
  // Standard component properties
  modalTitle = "8858 Schedule M: Transactions Between Foreign Disregarded Entity of a Foreign Tax Owner and the Filer or Other Related Entities";
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  // Component state
  dataLoading = false;
  crudLoading = false;
  userMessage = '';
  isInitialized = false;
  
  // Data properties
  irsF8858ScheduleMData: ScheduleMLineItem[] = [];
  irsF8858ScheduleMOriginalData: ScheduleMLineItem[] = [];
  adjAmts: { [key: number]: AdjustmentData } = {};
  resposneTempData: AdjustmentData[] = [];
  
  // Global configuration
  GENERAL_CONFIG: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private fir8858ScheduleMService: Fir8858ScheduleMService,
    private snackBar: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.loadCancel$.next();
    this.loadCancel$.complete();
    this.subscriptions.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    this.filterParams = fp;
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.globalService = {
      ...extractObject(this.gtwGlobal.global?.globalParams || {}),
      ...extractObject(hydrated)
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    if (Object.keys(rowObj).length) {
      this.rowData = rowObj;
    }
  }

  private createJsonGlobalWrapper(): any {
    return {
      globalParams: this.globalService || {},
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs
    };
  }

  private resetAndInit(): void {
    this.isInitialized = false;
    this.init();
  }

  init() {
    if (!this.isInitialized && 
        this.rowData && Object.keys(this.rowData).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.isInitialized = true;
      this.loadIRSF8858ScheduleMData();
    }
  }

  loadIRSF8858ScheduleMData() {
    this.loadCancel$.next();
    this.irsF8858ScheduleMData = [];
    
    const params = {
      action_code: 'atavd1',
      irs_form_no: 'F8858',
      jcd_key: this.globalService?.jcd_key === '' || this.globalService?.jcd_key === undefined ? 250 : this.globalService.jcd_key,
      combination_key: this.rowData.COMBINATION_KEY
    };
    
    this.dataLoading = true;
    
    const globalServiceWrapper = this.createJsonGlobalWrapper();

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, globalServiceWrapper, null, null)
      .pipe(takeUntil(this.loadCancel$), takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          if (data.callSuccess === "1") {
            this.dataLoading = false;
            this.irsF8858ScheduleMData = [];
            const irsF8858ScheduleMDataTemp = data.jsonObject;
            
            if (!irsF8858ScheduleMDataTemp || irsF8858ScheduleMDataTemp.length < 2) {
              return;
            }
            
            let i = 1;
            let previous_line_no = irsF8858ScheduleMDataTemp[i].line_no;
            
            while (i < irsF8858ScheduleMDataTemp.length) {
              const tempObj: any = {};
              let j = i;
              
              while (j < irsF8858ScheduleMDataTemp.length && previous_line_no == irsF8858ScheduleMDataTemp[j].line_no) {
                if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "LINE_NO") {
                  if (irsF8858ScheduleMDataTemp[j].LINE_TYPE == 'T1' || irsF8858ScheduleMDataTemp[j].LINE_TYPE == 'T2') {
                    tempObj.backgroudColor = {
                      "background": "#D6D6D6",
                      "font-weight": "bold"
                    };
                  } else {
                    tempObj.backgroudColor = {};
                  }
                  
                  tempObj.line_no = irsF8858ScheduleMDataTemp[j].line_no;
                  
                  if (irsF8858ScheduleMDataTemp[j].LINE_TYPE == 'T1' || irsF8858ScheduleMDataTemp[j].LINE_TYPE == 'T2') {
                    tempObj.line_type = 'total';
                  } else {
                    tempObj.line_type = irsF8858ScheduleMDataTemp[j].LINE_TYPE;
                  }
                  
                  if (irsF8858ScheduleMDataTemp[j].LINE_TYPE == 'T1') {
                    tempObj.total_line_type = 'T1';
                  } else if (irsF8858ScheduleMDataTemp[j].LINE_TYPE == 'T2') {
                    tempObj.total_line_type = 'T2';
                  }
                  
                  tempObj.line_multiplier = irsF8858ScheduleMDataTemp[j].MULTIPLIER;
                  
                  tempObj.saveParams = {
                    "tax_year": irsF8858ScheduleMDataTemp[j].object_id.split("~")[0],
                    "trans_type_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[1],
                    "form_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[6]
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "TRANSACTION_OF_FOREIGN_DISRGARDED_ENTITY") {
                  tempObj.line_Description = irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[3];
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "US_PERSON_FILING_THIS_RETURN_SYSTEM") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_b1 = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "US_PERSON_FILING_THIS_RETURN_OVERRIDE") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_b = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "DOMESTIC_CORPORATION_PARTNERSHIP_CONTROLLED_BY_FILER_SYSTEM") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_c1 = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "DOMESTIC_CORPORATION_PARTNERSHIP_CONTROLLED_BY_FILER_OVERRIDE") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_c = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "FOREIGN_CORPORATION_PARTNERSHIP_CONTROLLED_BY_FILER_SYSTEM") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_d1 = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "FOREIGN_CORPORATION_PARTNERSHIP_CONTROLLED_BY_FILER_OVERRIDE") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_d = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "TEN_PERCENT_OR_MORE_US_SHAREHOLDER_ANY_CORPORATION_CONTROLLING_TAX_OWNER") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_e = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                } else if (irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0] == "TEN_PERCENT_OR_MORE_US_SHAREHOLDER_OR_OTHER_OWNER_OF_ENTITY_CONTROLLING_TAX_OWNER") {
                  const value = irsF8858ScheduleMDataTemp[j].VALUE !== undefined ? 
                    (irsF8858ScheduleMDataTemp[j].MULTIPLIER * parseInt(irsF8858ScheduleMDataTemp[j].VALUE)) : null;
                  tempObj.col_f = {
                    "VALUE": value,
                    "line_attrib_key": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[1],
                    "attrib_name": irsF8858ScheduleMDataTemp[j].attrib_value.split("~")[0],
                    "trans_Details_key": irsF8858ScheduleMDataTemp[j].object_id.split("~")[3],
                    "occurrence": irsF8858ScheduleMDataTemp[j].object_id.split("~")[4],
                    "is_Update": 'N'
                  };
                }
                
                previous_line_no = irsF8858ScheduleMDataTemp[j].line_no;
                j++;
              }
              
              if (j < irsF8858ScheduleMDataTemp.length) {
                previous_line_no = irsF8858ScheduleMDataTemp[j].line_no;
              }
              
              const tempIndex = this.irsF8858ScheduleMData.length;
              this.irsF8858ScheduleMData[tempIndex] = tempObj;
              
              i = j;
            }
            
            // Convert line_no to numbers and sort
            for (let i = 0; i < this.irsF8858ScheduleMData.length; i++) {
              this.irsF8858ScheduleMData[i].line_no = Number(this.irsF8858ScheduleMData[i].line_no);
            }
            this.irsF8858ScheduleMData = _.sortBy(this.irsF8858ScheduleMData, 'line_no');
            
            this.getSchdMAdjAmt();
          } else {
            this.dataLoading = false;
            if (data.errorMessage === "no access") {
              this.snackBar.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red', true, 4);
              this.userMessage = "!! Unable to perform Selected Action...";
            } else {
              this.snackBar.openSnackBar('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red', true, 4);
              this.userMessage = "!! Unable to perform Selected Action...";
            }
          }
        },
        (error) => {
          this.dataLoading = false;
          this.snackBar.openSnackBar('Error loading data. Please try again.', 'gtw-snackbar--red', true, 4);
        }
      );
  }

  getSchdMAdjAmt() {
    this.adjAmts = {};
    
    const params = {
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year,
      combination_key: this.rowData.COMBINATION_KEY,
      scenario: this.filterParams?.scenario || this.globalService?.scenario,
      jcd_key: this.globalService?.jcd_key === '' || this.globalService?.jcd_key === undefined ? 250 : this.globalService.jcd_key
    };
    
    this.dataLoading = true;
    
    const globalServiceWrapper = this.createJsonGlobalWrapper();

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + "/loadJsonObject?action_code=ph95ti", params, globalServiceWrapper, null, null)
      .pipe(takeUntil(this.loadCancel$), takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          if (data.callSuccess === "1") {
            this.dataLoading = false;
            const resposneTempData = data.jsonObject;
            this.resposneTempData = data.jsonObject;
            
            for (let i = 0; i < resposneTempData.length; i++) {
              const index = resposneTempData[i].LINE_NO;
              this.adjAmts[index] = {
                "LINE_NO": resposneTempData[i].LINE_NO,
                "C_AMT": resposneTempData[i].C_AMT,
                "D_AMT": resposneTempData[i].D_AMT,
                "B_AMT": resposneTempData[i].B_AMT
              };
            }
            
            // Setting adj_amt to schedule M array
            for (let i = 0; i < this.irsF8858ScheduleMData.length; i++) {
              if (this.irsF8858ScheduleMData[i].line_type != 'filer') {
                const start_2 = _.find(this.irsF8858ScheduleMData, { line_type: 'start2' });
                const start_2_line_no = start_2 ? start_2.line_no : 0;
                const total_2 = _.find(this.irsF8858ScheduleMData, { total_line_type: 'T2' });
                const total_2_line_no = total_2 ? total_2.line_no : 0;
                
                const index = parseInt(this.irsF8858ScheduleMData[i].line_no.toString());
                
                // Handle col_b
                if (this.irsF8858ScheduleMData[i].col_b && this.irsF8858ScheduleMData[i].col_b!.VALUE !== undefined) {
                  if (index < start_2_line_no || index === total_2_line_no) {
                    this.irsF8858ScheduleMData[i].col_b1!.VALUE = (this.adjAmts[index]?.B_AMT == undefined ? null : this.adjAmts[index].B_AMT!);
                    if (this.irsF8858ScheduleMData[i].col_b!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_b!.VALUE = (this.adjAmts[index]?.B_AMT == undefined ? null : this.adjAmts[index].B_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_b!.VALUE = this.irsF8858ScheduleMData[i].col_b!.VALUE;
                    }
                  } else {
                    this.irsF8858ScheduleMData[i].col_b1!.VALUE = (this.adjAmts[index]?.B_AMT == undefined ? null : this.adjAmts[index].B_AMT!);
                    if (this.irsF8858ScheduleMData[i].col_b!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_b!.VALUE = (this.adjAmts[index]?.B_AMT == undefined ? null : this.adjAmts[index].B_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_b!.VALUE = this.irsF8858ScheduleMData[i].col_b!.VALUE;
                    }
                  }
                }
                
                // Handle col_c
                if (this.irsF8858ScheduleMData[i].col_c && this.irsF8858ScheduleMData[i].col_c!.VALUE !== undefined) {
                  if (index < start_2_line_no || index === total_2_line_no) {
                    this.irsF8858ScheduleMData[i].col_c1!.VALUE = (this.adjAmts[index]?.C_AMT == undefined ? null : this.adjAmts[index].C_AMT!);
                    if (this.irsF8858ScheduleMData[i].col_c!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_c!.VALUE = (this.adjAmts[index]?.C_AMT == undefined ? null : this.adjAmts[index].C_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_c!.VALUE = this.irsF8858ScheduleMData[i].col_c!.VALUE;
                    }
                  } else {
                    this.irsF8858ScheduleMData[i].col_c1!.VALUE = (this.adjAmts[index]?.C_AMT == undefined ? null : this.adjAmts[index].C_AMT!);
                    if (this.irsF8858ScheduleMData[i].col_c!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_c!.VALUE = (this.adjAmts[index]?.C_AMT == undefined ? null : this.adjAmts[index].C_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_c!.VALUE = this.irsF8858ScheduleMData[i].col_c!.VALUE;
                    }
                  }
                }
                
                // Handle col_d
                if (this.irsF8858ScheduleMData[i].col_d && this.irsF8858ScheduleMData[i].col_d!.VALUE !== undefined) {
                  if (index < start_2_line_no || index === total_2_line_no) {
                    this.irsF8858ScheduleMData[i].col_d1!.VALUE = (this.adjAmts[index]?.D_AMT == undefined ? null : this.adjAmts[index].D_AMT!);
                    if (this.irsF8858ScheduleMData[i].col_d!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_d!.VALUE = (this.adjAmts[index]?.D_AMT == undefined ? null : this.adjAmts[index].D_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_d!.VALUE = this.irsF8858ScheduleMData[i].col_d!.VALUE;
                    }
                  } else {
                    this.irsF8858ScheduleMData[i].col_d1!.VALUE = (this.adjAmts[index]?.D_AMT == undefined ? null : this.adjAmts[index].D_AMT!);
                    if (this.irsF8858ScheduleMData[i].col_d!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_d!.VALUE = (this.adjAmts[index]?.D_AMT == undefined ? null : this.adjAmts[index].D_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_d!.VALUE = this.irsF8858ScheduleMData[i].col_d!.VALUE;
                    }
                  }
                }
                
                // Handle col_e
                if (this.irsF8858ScheduleMData[i].col_e && this.irsF8858ScheduleMData[i].col_e!.VALUE !== undefined) {
                  if (index < start_2_line_no || index === total_2_line_no) {
                    if (this.irsF8858ScheduleMData[i].col_e!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_e!.VALUE = (this.adjAmts[index]?.E_AMT == undefined ? null : this.adjAmts[index].E_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_e!.VALUE = this.irsF8858ScheduleMData[i].col_e!.VALUE;
                    }
                  } else {
                    if (this.irsF8858ScheduleMData[i].col_e!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_e!.VALUE = (this.adjAmts[index]?.E_AMT == undefined ? null : this.adjAmts[index].E_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_e!.VALUE = this.irsF8858ScheduleMData[i].col_e!.VALUE;
                    }
                  }
                }
                
                // Handle col_f
                if (this.irsF8858ScheduleMData[i].col_f && this.irsF8858ScheduleMData[i].col_f!.VALUE !== undefined) {
                  if (index < start_2_line_no || index === total_2_line_no) {
                    if (this.irsF8858ScheduleMData[i].col_f!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_f!.VALUE = (this.adjAmts[index]?.F_AMT == undefined ? null : this.adjAmts[index].F_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_f!.VALUE = this.irsF8858ScheduleMData[i].col_f!.VALUE;
                    }
                  } else {
                    if (this.irsF8858ScheduleMData[i].col_f!.trans_Details_key == "0") {
                      this.irsF8858ScheduleMData[i].col_f!.VALUE = (this.adjAmts[index]?.F_AMT == undefined ? null : this.adjAmts[index].F_AMT!);
                    } else {
                      this.irsF8858ScheduleMData[i].col_f!.VALUE = this.irsF8858ScheduleMData[i].col_f!.VALUE;
                    }
                  }
                }
              }
            }
            
            // Updating total after override adj_amts
            this.computeColValues('b1', 1);
            this.computeColValues('b', 1);
            this.computeColValues('c1', 1);
            this.computeColValues('c', 1);
            this.computeColValues('d1', 1);
            this.computeColValues('d', 1);
            this.computeColValues('c1', 11);
            this.computeColValues('c', 11);
            this.computeColValues('d1', 11);
            this.computeColValues('d', 11);
            this.computeColValues('b', 11);
            this.computeColValues('b1', 11);
            this.computeColValues('e', 11);
            this.computeColValues('f', 11);
            this.computeColValues('e', 1);
            this.computeColValues('f', 1);
            
            // Backup for reset
            this.irsF8858ScheduleMOriginalData = _.cloneDeep(this.irsF8858ScheduleMData);
          } else {
            this.dataLoading = false;
            if (data.errorMessage === "no access") {
              this.snackBar.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red', true, 4);
              this.userMessage = "!! Unable to perform Selected Action...";
            } else {
              this.snackBar.openSnackBar('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red', true, 4);
              this.userMessage = "!! Unable to perform Selected Action...";
            }
          }
        },
        (error) => {
          this.dataLoading = false;
          this.snackBar.openSnackBar('Error loading adjustment amounts. Please try again.', 'gtw-snackbar--red', true, 4);
        }
      );
  }

  computeColValues(col_name: string, v_line_no: number, value?: any) {
    const line_T1 = _.find(this.irsF8858ScheduleMData, { total_line_type: 'T1' });
    const total_line_T1 = line_T1 ? line_T1.line_no : 0;
    const line_T2 = _.find(this.irsF8858ScheduleMData, { total_line_type: 'T2' });
    const total_line_T2 = line_T2 ? line_T2.line_no : 0;
    
    if (col_name !== undefined && v_line_no < total_line_T2) {
      for (let i = 0; i < this.resposneTempData.length; i++) {
        let total_line: any;
        
        if (v_line_no < total_line_T1) {
          total_line = _.find(this.irsF8858ScheduleMData, { line_no: total_line_T1 });
        } else if (v_line_no > total_line_T1 && v_line_no < total_line_T2) {
          total_line = _.find(this.irsF8858ScheduleMData, { line_no: total_line_T2 });
        }
        
        const temp_line_obj_update = _.find(this.irsF8858ScheduleMData, { line_no: v_line_no });
        
        if (temp_line_obj_update !== undefined && parseInt(temp_line_obj_update.line_no.toString()) == parseInt(this.resposneTempData[i].LINE_NO.toString())) {
          // Handle col_b
          if (col_name === 'b' || col_name === 'b1') {
            const temp_col_obj1 = temp_line_obj_update.col_b;
            const temp_total_col_obj1 = total_line?.col_b;
            
            if (temp_col_obj1 !== undefined) {
              const b_amt = this.resposneTempData[i].B_AMT == undefined ? null : (-1 * (this.resposneTempData[i].B_AMT!)).toString();
              const col_value = temp_col_obj1.VALUE == undefined ? null : (temp_col_obj1.VALUE).toString();
              
              if (b_amt == null && col_value == null) {
                temp_col_obj1.is_Update = 'N';
                if (temp_total_col_obj1) temp_total_col_obj1.is_Update = 'Y';
              } else if (col_value !== undefined && col_value !== b_amt) {
                temp_col_obj1.is_Update = 'Y';
                if (temp_total_col_obj1) temp_total_col_obj1.is_Update = 'Y';
              } else if (temp_col_obj1.trans_Details_key !== '0') {
                temp_col_obj1.is_Update = 'Y';
                if (temp_total_col_obj1) temp_total_col_obj1.is_Update = 'Y';
              } else {
                temp_col_obj1.is_Update = 'N';
                if (temp_total_col_obj1) temp_total_col_obj1.is_Update = 'Y';
              }
            }
          }
          
          // Handle col_c
          if (col_name === 'c' || col_name === 'c1') {
            const temp_col_obj2 = temp_line_obj_update.col_c;
            const temp_total_col_obj2 = total_line?.col_c;
            
            if (temp_col_obj2 !== undefined) {
              const c_amt = this.resposneTempData[i].C_AMT == undefined ? null : (-1 * (this.resposneTempData[i].C_AMT!)).toString();
              const col_value = temp_col_obj2.VALUE == undefined ? null : (temp_col_obj2.VALUE).toString();
              
              if (c_amt == null && col_value == null) {
                temp_col_obj2.is_Update = 'N';
                if (temp_total_col_obj2) temp_total_col_obj2.is_Update = 'Y';
              } else if (col_value !== undefined && col_value !== c_amt) {
                temp_col_obj2.is_Update = 'Y';
                if (temp_total_col_obj2) temp_total_col_obj2.is_Update = 'Y';
              } else if (temp_col_obj2.trans_Details_key !== '0') {
                temp_col_obj2.is_Update = 'Y';
                if (temp_total_col_obj2) temp_total_col_obj2.is_Update = 'Y';
              } else {
                temp_col_obj2.is_Update = 'N';
                if (temp_total_col_obj2) temp_total_col_obj2.is_Update = 'Y';
              }
            }
          }
          
          // Handle col_d
          if (col_name === 'd' || col_name === 'd1') {
            const temp_col_obj3 = temp_line_obj_update.col_d;
            const temp_total_col_obj3 = total_line?.col_d;
            
            if (temp_col_obj3 !== undefined) {
              const d_amt = this.resposneTempData[i].D_AMT == undefined ? null : (-1 * (this.resposneTempData[i].D_AMT!)).toString();
              const col_value = temp_col_obj3.VALUE == undefined ? null : (temp_col_obj3.VALUE).toString();
              
              if (d_amt == null && col_value == null) {
                temp_col_obj3.is_Update = 'N';
                if (temp_total_col_obj3) temp_total_col_obj3.is_Update = 'N';
              } else if (col_value !== undefined && col_value !== d_amt) {
                temp_col_obj3.is_Update = 'Y';
                if (temp_total_col_obj3) temp_total_col_obj3.is_Update = 'Y';
              } else if (temp_col_obj3.trans_Details_key !== '0') {
                temp_col_obj3.is_Update = 'Y';
                if (temp_total_col_obj3) temp_total_col_obj3.is_Update = 'Y';
              } else {
                temp_col_obj3.is_Update = 'N';
                if (temp_total_col_obj3) temp_total_col_obj3.is_Update = 'Y';
              }
            }
          }
          
          // Handle col_e
          if (col_name === 'e') {
            const temp_col_obj4 = temp_line_obj_update.col_e;
            const temp_total_col_obj4 = total_line?.col_e;
            
            if (temp_col_obj4) {
              const col_value = temp_col_obj4.VALUE == undefined ? null : (temp_col_obj4.VALUE).toString();
              
              if (col_value !== undefined && col_value !== null) {
                temp_col_obj4.is_Update = 'Y';
                if (temp_total_col_obj4) temp_total_col_obj4.is_Update = 'Y';
              } else if (temp_col_obj4.trans_Details_key !== '0') {
                temp_col_obj4.is_Update = 'Y';
                if (temp_total_col_obj4) temp_total_col_obj4.is_Update = 'Y';
              } else {
                temp_col_obj4.is_Update = 'N';
                if (temp_total_col_obj4) temp_total_col_obj4.is_Update = 'Y';
              }
            }
          }
          
          // Handle col_f
          if (col_name === 'f') {
            const temp_col_obj5 = temp_line_obj_update.col_f;
            const temp_total_col_obj5 = total_line?.col_f;
            
            if (temp_col_obj5) {
              const col_value = temp_col_obj5.VALUE == undefined ? null : (temp_col_obj5.VALUE).toString();
              
              if (col_value !== undefined && col_value !== null) {
                temp_col_obj5.is_Update = 'Y';
                if (temp_total_col_obj5) temp_total_col_obj5.is_Update = 'Y';
              } else if (temp_col_obj5.trans_Details_key !== '0') {
                temp_col_obj5.is_Update = 'Y';
                if (temp_total_col_obj5) temp_total_col_obj5.is_Update = 'Y';
              } else {
                temp_col_obj5.is_Update = 'N';
                if (temp_total_col_obj5) temp_total_col_obj5.is_Update = 'Y';
              }
            }
          }
        }
      }
    }
    
    if (col_name !== undefined && v_line_no > total_line_T2) {
      for (let i = 0; i < this.resposneTempData.length; i++) {
        const temp_line_obj_update = _.find(this.irsF8858ScheduleMData, { line_no: v_line_no });
        
        if (temp_line_obj_update !== undefined && parseInt(temp_line_obj_update.line_no.toString()) == parseInt(this.resposneTempData[i].LINE_NO.toString())) {
          // Handle col_b
          const temp_col_obj1 = temp_line_obj_update.col_b;
          if (temp_col_obj1 !== undefined) {
            const b_amt = this.resposneTempData[i].B_AMT == undefined ? null : (-1 * (this.resposneTempData[i].B_AMT!)).toString();
            const col_value = temp_col_obj1.VALUE == undefined ? null : (temp_col_obj1.VALUE).toString();
            
            if (b_amt == null && col_value == null) {
              temp_col_obj1.is_Update = 'N';
            } else if (col_value !== undefined && col_value !== b_amt) {
              temp_col_obj1.is_Update = 'Y';
            } else if (temp_col_obj1.trans_Details_key !== '0') {
              temp_col_obj1.is_Update = 'Y';
            } else {
              temp_col_obj1.is_Update = 'N';
            }
          }
          
          // Handle col_c
          const temp_col_obj2 = temp_line_obj_update.col_c;
          if (temp_col_obj2 !== undefined) {
            const c_amt = this.resposneTempData[i].C_AMT == undefined ? null : (-1 * (this.resposneTempData[i].C_AMT!)).toString();
            const col_value = temp_col_obj2.VALUE == undefined ? null : (temp_col_obj2.VALUE).toString();
            
            if (c_amt == null && col_value == null) {
              temp_col_obj2.is_Update = 'N';
            } else if (col_value !== undefined && col_value !== c_amt) {
              temp_col_obj2.is_Update = 'Y';
            } else if (temp_col_obj2.trans_Details_key !== '0') {
              temp_col_obj2.is_Update = 'Y';
            } else {
              temp_col_obj2.is_Update = 'N';
            }
          }
          
          // Handle col_d
          const temp_col_obj3 = temp_line_obj_update.col_d;
          if (temp_col_obj3 !== undefined) {
            const d_amt = this.resposneTempData[i].D_AMT == undefined ? null : (-1 * (this.resposneTempData[i].D_AMT!)).toString();
            const col_value = temp_col_obj3.VALUE == undefined ? null : (temp_col_obj3.VALUE).toString();
            
            if (d_amt == null && col_value == null) {
              temp_col_obj3.is_Update = 'N';
            } else if (col_value !== undefined && col_value !== d_amt) {
              temp_col_obj3.is_Update = 'Y';
            } else if (temp_col_obj3.trans_Details_key !== '0') {
              temp_col_obj3.is_Update = 'Y';
            } else {
              temp_col_obj3.is_Update = 'N';
            }
          }
          
          // Handle col_e
          const temp_col_obj4 = temp_line_obj_update.col_e;
          if (temp_col_obj4 !== undefined) {
            const col_value = temp_col_obj4.VALUE == undefined ? null : (temp_col_obj4.VALUE).toString();
            
            if (col_value !== undefined && col_value !== null) {
              temp_col_obj4.is_Update = 'Y';
            } else if (temp_col_obj4.trans_Details_key !== '0') {
              temp_col_obj4.is_Update = 'Y';
            } else {
              temp_col_obj4.is_Update = 'N';
            }
          }
          
          // Handle col_f
          const temp_col_obj5 = temp_line_obj_update.col_f;
          if (temp_col_obj5 !== undefined) {
            const col_value = temp_col_obj5.VALUE == undefined ? null : (temp_col_obj5.VALUE).toString();
            
            if (col_value !== undefined && col_value !== null) {
              temp_col_obj5.is_Update = 'Y';
            } else if (temp_col_obj5.trans_Details_key !== '0') {
              temp_col_obj5.is_Update = 'Y';
            } else {
              temp_col_obj5.is_Update = 'N';
            }
          }
        }
      }
    }
    
    // Calculate totals
    let total: number | null = null;
    let endLine_no: number | null = null;
    let startLine_no: number | null = null;
    
    if (parseInt(v_line_no.toString()) < total_line_T1) {
      const start_line1 = _.find(this.irsF8858ScheduleMData, { line_type: 'start1' });
      startLine_no = start_line1 ? start_line1.line_no : null;
      endLine_no = total_line_T1;
    } else if (parseInt(v_line_no.toString()) > total_line_T1) {
      const start_line2 = _.find(this.irsF8858ScheduleMData, { line_type: 'start2' });
      startLine_no = start_line2 ? start_line2.line_no : null;
      endLine_no = total_line_T2;
    }
    
    let i: number | null = null;
    for (let k = 0; k < this.irsF8858ScheduleMData.length; k++) {
      if (this.irsF8858ScheduleMData[k].line_no.toString() == startLine_no?.toString()) {
        i = k;
        break;
      }
    }
    
    if (i !== null && startLine_no !== null && endLine_no !== null) {
      while (i < this.irsF8858ScheduleMData.length && this.irsF8858ScheduleMData[i].line_no != endLine_no) {
        if (this.irsF8858ScheduleMData[i] !== undefined) {
          if (col_name == 'b') {
            if (this.irsF8858ScheduleMData[i].col_b && this.irsF8858ScheduleMData[i].col_b!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_b!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_b!.VALUE!.toString());
            }
          } else if (col_name == 'b1') {
            if (this.irsF8858ScheduleMData[i].col_b1 && this.irsF8858ScheduleMData[i].col_b1!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_b1!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_b1!.VALUE!.toString());
            }
          } else if (col_name == 'c') {
            if (this.irsF8858ScheduleMData[i].col_c && this.irsF8858ScheduleMData[i].col_c!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_c!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_c!.VALUE!.toString());
            }
          } else if (col_name == 'c1') {
            if (this.irsF8858ScheduleMData[i].col_c1 && this.irsF8858ScheduleMData[i].col_c1!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_c1!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_c1!.VALUE!.toString());
            }
          } else if (col_name == 'd1') {
            if (this.irsF8858ScheduleMData[i].col_d1 && this.irsF8858ScheduleMData[i].col_d1!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_d1!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_d1!.VALUE!.toString());
            }
          } else if (col_name == 'd') {
            if (this.irsF8858ScheduleMData[i].col_d && this.irsF8858ScheduleMData[i].col_d!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_d!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_d!.VALUE!.toString());
            }
          } else if (col_name == 'e') {
            if (this.irsF8858ScheduleMData[i].col_e && this.irsF8858ScheduleMData[i].col_e!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_e!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_e!.VALUE!.toString());
            }
          } else if (col_name == 'f') {
            if (this.irsF8858ScheduleMData[i].col_f && this.irsF8858ScheduleMData[i].col_f!.VALUE !== undefined && this.irsF8858ScheduleMData[i].col_f!.VALUE != null) {
              total = (total || 0) + parseInt(this.irsF8858ScheduleMData[i].col_f!.VALUE!.toString());
            }
          }
        }
        i++;
      }
      
      if (i != null) {
        switch (col_name) {
          case 'b':
            if (this.irsF8858ScheduleMData[i].col_b) {
              this.irsF8858ScheduleMData[i].col_b!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
          case 'c':
            if (this.irsF8858ScheduleMData[i].col_c) {
              this.irsF8858ScheduleMData[i].col_c!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
          case 'b1':
            if (this.irsF8858ScheduleMData[i].col_b1) {
              this.irsF8858ScheduleMData[i].col_b1!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
          case 'c1':
            if (this.irsF8858ScheduleMData[i].col_c1) {
              this.irsF8858ScheduleMData[i].col_c1!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
          case 'd1':
            if (this.irsF8858ScheduleMData[i].col_d1) {
              this.irsF8858ScheduleMData[i].col_d1!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
          case 'd':
            if (this.irsF8858ScheduleMData[i].col_d) {
              this.irsF8858ScheduleMData[i].col_d!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
          case 'e':
            if (this.irsF8858ScheduleMData[i].col_e) {
              this.irsF8858ScheduleMData[i].col_e!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
          case 'f':
            if (this.irsF8858ScheduleMData[i].col_f) {
              this.irsF8858ScheduleMData[i].col_f!.VALUE = total == null ? total : parseInt(total.toString());
            }
            break;
        }
      }
    }
  }

  save() {
    if (this.crudLoading) {
      this.snackBar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow', true, 4);
      return;
    }
    
    this.isSaveClicked = true;
    this.crudLoading = true;
    
    const editRow: any[] = [];
    
    const scheduleSettings = [{
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year,
      "scenario": this.filterParams?.scenario || this.globalService?.scenario,
      "jcd_key": this.globalService?.jcd_key === '' || this.globalService?.jcd_key === undefined ? 250 : this.globalService.jcd_key
    }];
    
    for (let i = 0; i < this.irsF8858ScheduleMData.length; i++) {
      
      // Handle col_b
      if (this.irsF8858ScheduleMData[i].col_b && this.irsF8858ScheduleMData[i].col_b!.is_Update == 'Y') {
        const tempObj = [{
          "tax_year": this.irsF8858ScheduleMData[i].saveParams.tax_year,
          "trans_type_key": this.irsF8858ScheduleMData[i].saveParams.trans_type_key,
          "combination_key": this.rowData.COMBINATION_KEY,
          "group_obj_key": this.rowData.GROUP_OBJ_KEY,
          "form_key": this.irsF8858ScheduleMData[i].saveParams.form_key,
          "line_no": this.irsF8858ScheduleMData[i].line_no,
          "occurence": ((this.irsF8858ScheduleMData[i].col_b!.occurrence == "0") ? null : this.irsF8858ScheduleMData[i].col_b!.occurrence),
          "line_attrib_key": this.irsF8858ScheduleMData[i].col_b!.line_attrib_key,
          "Attribute_Name": this.irsF8858ScheduleMData[i].col_b!.attrib_name,
          "Attribute_Value": (this.irsF8858ScheduleMData[i].col_b!.VALUE != null ? this.irsF8858ScheduleMData[i].col_b!.VALUE! * this.irsF8858ScheduleMData[i].line_multiplier : ''),
          "trans_dtls_key": ((this.irsF8858ScheduleMData[i].col_b!.trans_Details_key == "0") ? null : this.irsF8858ScheduleMData[i].col_b!.trans_Details_key),
          "trans_status": "A"
        }];
        editRow.push(tempObj);
      }
      
      // Handle col_c
      if (this.irsF8858ScheduleMData[i].col_c && this.irsF8858ScheduleMData[i].col_c!.is_Update == 'Y') {
        const tempObj = [{
          "tax_year": this.irsF8858ScheduleMData[i].saveParams.tax_year,
          "trans_type_key": this.irsF8858ScheduleMData[i].saveParams.trans_type_key,
          "combination_key": this.rowData.COMBINATION_KEY,
          "group_obj_key": this.rowData.GROUP_OBJ_KEY,
          "form_key": this.irsF8858ScheduleMData[i].saveParams.form_key,
          "line_no": this.irsF8858ScheduleMData[i].line_no,
          "occurence": ((this.irsF8858ScheduleMData[i].col_c!.occurrence == "0") ? null : this.irsF8858ScheduleMData[i].col_c!.occurrence),
          "line_attrib_key": this.irsF8858ScheduleMData[i].col_c!.line_attrib_key,
          "Attribute_Name": this.irsF8858ScheduleMData[i].col_c!.attrib_name,
          "Attribute_Value": (this.irsF8858ScheduleMData[i].col_c!.VALUE != null ? this.irsF8858ScheduleMData[i].col_c!.VALUE! * this.irsF8858ScheduleMData[i].line_multiplier : ''),
          "trans_dtls_key": ((this.irsF8858ScheduleMData[i].col_c!.trans_Details_key == "0") ? null : this.irsF8858ScheduleMData[i].col_c!.trans_Details_key),
          "trans_status": "A"
        }];
        editRow.push(tempObj);
      }
      
      // Handle col_d
      if (this.irsF8858ScheduleMData[i].col_d && this.irsF8858ScheduleMData[i].col_d!.is_Update == 'Y') {
        const tempObj = [{
          "tax_year": this.irsF8858ScheduleMData[i].saveParams.tax_year,
          "trans_type_key": this.irsF8858ScheduleMData[i].saveParams.trans_type_key,
          "combination_key": this.rowData.COMBINATION_KEY,
          "group_obj_key": this.rowData.GROUP_OBJ_KEY,
          "form_key": this.irsF8858ScheduleMData[i].saveParams.form_key,
          "line_no": this.irsF8858ScheduleMData[i].line_no,
          "occurence": ((this.irsF8858ScheduleMData[i].col_d!.occurrence == "0") ? null : this.irsF8858ScheduleMData[i].col_d!.occurrence),
          "line_attrib_key": this.irsF8858ScheduleMData[i].col_d!.line_attrib_key,
          "Attribute_Name": this.irsF8858ScheduleMData[i].col_d!.attrib_name,
          "Attribute_Value": (this.irsF8858ScheduleMData[i].col_d!.VALUE != null ? this.irsF8858ScheduleMData[i].col_d!.VALUE! * this.irsF8858ScheduleMData[i].line_multiplier : ''),
          "trans_dtls_key": ((this.irsF8858ScheduleMData[i].col_d!.trans_Details_key == "0") ? null : this.irsF8858ScheduleMData[i].col_d!.trans_Details_key),
          "trans_status": "A"
        }];
        editRow.push(tempObj);
      }
      
      // Handle col_e
      if (this.irsF8858ScheduleMData[i].col_e && this.irsF8858ScheduleMData[i].col_e!.is_Update == 'Y') {
        const tempObj = [{
          "tax_year": this.irsF8858ScheduleMData[i].saveParams.tax_year,
          "trans_type_key": this.irsF8858ScheduleMData[i].saveParams.trans_type_key,
          "combination_key": this.rowData.COMBINATION_KEY,
          "group_obj_key": this.rowData.GROUP_OBJ_KEY,
          "form_key": this.irsF8858ScheduleMData[i].saveParams.form_key,
          "line_no": this.irsF8858ScheduleMData[i].line_no,
          "occurence": ((this.irsF8858ScheduleMData[i].col_e!.occurrence == "0") ? null : this.irsF8858ScheduleMData[i].col_e!.occurrence),
          "line_attrib_key": this.irsF8858ScheduleMData[i].col_e!.line_attrib_key,
          "Attribute_Name": this.irsF8858ScheduleMData[i].col_e!.attrib_name,
          "Attribute_Value": (this.irsF8858ScheduleMData[i].col_e!.VALUE != null ? this.irsF8858ScheduleMData[i].col_e!.VALUE! * this.irsF8858ScheduleMData[i].line_multiplier : ''),
          "trans_dtls_key": ((this.irsF8858ScheduleMData[i].col_e!.trans_Details_key == "0") ? null : this.irsF8858ScheduleMData[i].col_e!.trans_Details_key),
          "trans_status": "A"
        }];
        editRow.push(tempObj);
      }
      
      // Handle col_f
      if (this.irsF8858ScheduleMData[i].col_f && this.irsF8858ScheduleMData[i].col_f!.is_Update == 'Y') {
        const tempObj = [{
          "tax_year": this.irsF8858ScheduleMData[i].saveParams.tax_year,
          "trans_type_key": this.irsF8858ScheduleMData[i].saveParams.trans_type_key,
          "combination_key": this.rowData.COMBINATION_KEY,
          "group_obj_key": this.rowData.GROUP_OBJ_KEY,
          "form_key": this.irsF8858ScheduleMData[i].saveParams.form_key,
          "line_no": this.irsF8858ScheduleMData[i].line_no,
          "occurence": ((this.irsF8858ScheduleMData[i].col_f!.occurrence == "0") ? null : this.irsF8858ScheduleMData[i].col_f!.occurrence),
          "line_attrib_key": this.irsF8858ScheduleMData[i].col_f!.line_attrib_key,
          "Attribute_Name": this.irsF8858ScheduleMData[i].col_f!.attrib_name,
          "Attribute_Value": (this.irsF8858ScheduleMData[i].col_f!.VALUE != null ? this.irsF8858ScheduleMData[i].col_f!.VALUE! * this.irsF8858ScheduleMData[i].line_multiplier : ''),
          "trans_dtls_key": ((this.irsF8858ScheduleMData[i].col_f!.trans_Details_key == "0") ? null : this.irsF8858ScheduleMData[i].col_f!.trans_Details_key),
          "trans_status": "A"
        }];
        editRow.push(tempObj);
      }
    }
    
    const scheduleDetails = { "addrows": [], "editrows": editRow, "removerows": [] };
    
    const message = "Schedule M details are successfully saved";
    
    this.fir8858ScheduleMService.save8858ScheduleM(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleDetails,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.USER_SETTINGS,
      this.SERVER_CONFIG
    ).pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          
          if (result.errorMessage && result.errorMessage !== 'null') {
            this.snackBar.openSnackBar(result.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.snackBar.openSnackBar(message, 'gtw-snackbar--green', true, 4);
            this.loadIRSF8858ScheduleMData();
            // Do NOT emit 'refresh' - keep modal open after save (like original AngularJS component)
            // this.requestGridUpdate.emit('refresh');
          }
        },
        (error: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.snackBar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        }
      );
  }

  reset() {
    this.crudLoading = false;
    this.irsF8858ScheduleMData = _.cloneDeep(this.irsF8858ScheduleMOriginalData);
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
